import { z } from "zod";
import { MedicationCatalogSchema, MedicationSafetyContextSchema, MedicationSafetyEvaluationSchema, MedicationSafetyRuleSetSchema, StructuredPrescriptionPayloadSchema, } from "./medications.js";
import { IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
export const MedicationSourceAuthoritySchema = z.enum([
    "RxNorm_current_prescribable",
    "RxNorm_full_release",
    "DailyMed_SPL",
    "licensed_clinical_knowledge_base",
]);
export const MedicationLicenseClassSchema = z.enum([
    "public_domain",
    "umls_license",
    "commercial_license",
]);
export const MedicationImportArtifactSchema = z
    .object({
    artifactId: IdSchema,
    authority: MedicationSourceAuthoritySchema,
    releaseIdentifier: z.string().min(1).max(160),
    sourceUri: z.string().url().max(2_000),
    retrievedAt: IsoDateTimeSchema,
    contentHash: Sha256Schema,
    byteLength: z.number().int().positive(),
    licenseClass: MedicationLicenseClassSchema,
    licenseReference: z.string().min(1).max(500),
    artifactKind: z.enum(["rrf_archive", "spl_xml", "json_export", "rule_export"]),
})
    .strict();
export const MedicationImportManifestSchema = z
    .object({
    manifestId: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    status: z.enum(["staged", "validated", "rejected", "promoted"]),
    dataStatus: z.literal("authoritative_import"),
    createdAt: IsoDateTimeSchema,
    createdBy: IdSchema,
    artifacts: z.array(MedicationImportArtifactSchema).min(1).max(100),
    recordCounts: z
        .object({
        sourceRecords: z.number().int().nonnegative(),
        acceptedRecords: z.number().int().nonnegative(),
        rejectedRecords: z.number().int().nonnegative(),
    })
        .strict(),
    catalogueContentHash: Sha256Schema.optional(),
    safetyContentHash: Sha256Schema.optional(),
    manifestContentHash: Sha256Schema,
})
    .strict()
    .superRefine((manifest, context) => {
    if (manifest.recordCounts.acceptedRecords + manifest.recordCounts.rejectedRecords !==
        manifest.recordCounts.sourceRecords) {
        context.addIssue({
            code: "custom",
            path: ["recordCounts"],
            message: "Accepted and rejected import counts must equal the source count",
        });
    }
});
export const MedicationImportStageReceiptSchema = z
    .object({
    manifestId: IdSchema,
    status: z.literal("staged"),
    manifestContentHash: Sha256Schema,
})
    .strict();
export const MedicationImportDecisionRequestSchema = z
    .object({ decision: z.enum(["validated", "rejected"]) })
    .strict();
export const MedicationImportDecisionReceiptSchema = z
    .object({
    manifestId: IdSchema,
    status: z.enum(["validated", "rejected"]),
})
    .strict();
const RequiredMedicationSafetyKinds = [
    "allergy",
    "interaction",
    "duplicate_therapy",
    "dose",
    "route",
    "renal",
    "hepatic",
    "pregnancy",
    "age",
    "monitoring",
];
export const MedicationContentImportBundleSchema = z
    .object({
    manifest: MedicationImportManifestSchema,
    catalog: MedicationCatalogSchema,
    safetyRuleSet: MedicationSafetyRuleSetSchema,
})
    .strict()
    .superRefine((bundle, context) => {
    const { manifest, catalog, safetyRuleSet } = bundle;
    if (manifest.tenantId !== catalog.tenantId ||
        manifest.tenantId !== safetyRuleSet.tenantId ||
        manifest.siteId !== catalog.siteId ||
        manifest.siteId !== safetyRuleSet.siteId) {
        context.addIssue({
            code: "custom",
            path: ["manifest"],
            message: "Imported medication content must share one exact tenant and site",
        });
    }
    if (manifest.status !== "staged" ||
        catalog.dataStatus !== "authoritative_import" ||
        safetyRuleSet.dataStatus !== "authoritative_import" ||
        catalog.status !== "active" ||
        safetyRuleSet.status !== "active" ||
        !safetyRuleSet.productionEligible) {
        context.addIssue({
            code: "custom",
            path: ["catalog"],
            message: "Content imports must be staged authoritative production candidates",
        });
    }
    if (manifest.catalogueContentHash !== catalog.contentHash ||
        manifest.safetyContentHash !== safetyRuleSet.contentHash) {
        context.addIssue({
            code: "custom",
            path: ["manifest"],
            message: "Import manifest hashes must bind the exact catalogue and safety rule set",
        });
    }
    if (catalog.products.length === 0 ||
        !catalog.products.some((product) => product.active) ||
        safetyRuleSet.rules.length === 0) {
        context.addIssue({
            code: "custom",
            path: ["catalog"],
            message: "Authoritative medication imports require products and safety rules",
        });
    }
    const ruleKinds = new Set(safetyRuleSet.rules.filter((rule) => rule.active).map((rule) => rule.parameters.kind));
    for (const kind of RequiredMedicationSafetyKinds) {
        if (!ruleKinds.has(kind)) {
            context.addIssue({
                code: "custom",
                path: ["safetyRuleSet", "rules"],
                message: `Authoritative safety content is missing the ${kind} category`,
            });
        }
    }
    const release = catalog.terminologyRelease;
    const rxNormArtifact = manifest.artifacts.find((artifact) => (artifact.authority === "RxNorm_current_prescribable" ||
        artifact.authority === "RxNorm_full_release") &&
        artifact.releaseIdentifier === release.releaseIdentifier &&
        artifact.contentHash === release.contentHash);
    if (release.authority !== "RxNorm" ||
        release.status !== "active" ||
        rxNormArtifact === undefined) {
        context.addIssue({
            code: "custom",
            path: ["catalog", "terminologyRelease"],
            message: "Catalogue terminology must bind the exact active manifested RxNorm release",
        });
    }
    const dailyMedArtifact = manifest.artifacts.find((artifact) => artifact.authority === "DailyMed_SPL" && artifact.licenseClass === "public_domain");
    const knowledgeArtifact = manifest.artifacts.find((artifact) => artifact.authority === "licensed_clinical_knowledge_base" &&
        artifact.licenseClass === "commercial_license");
    const rxNormLicenseValid = rxNormArtifact === undefined ||
        (rxNormArtifact.authority === "RxNorm_current_prescribable"
            ? rxNormArtifact.licenseClass === "public_domain"
            : rxNormArtifact.licenseClass === "umls_license");
    if (!rxNormLicenseValid || dailyMedArtifact === undefined || knowledgeArtifact === undefined) {
        context.addIssue({
            code: "custom",
            path: ["manifest", "artifacts"],
            message: "Authoritative content requires correctly licensed RxNorm, DailyMed, and clinical knowledge artifacts",
        });
    }
});
export const MedicationContentImportReceiptSchema = MedicationImportStageReceiptSchema.extend({
    catalogId: IdSchema,
    catalogVersion: z.number().int().positive(),
    safetyRuleSetId: IdSchema,
    safetyRuleSetVersion: z.number().int().positive(),
}).strict();
export const MedicationEvaluationCaseSchema = z
    .object({
    caseId: z.string().regex(/^[A-Z][A-Z0-9_]{2,99}$/),
    category: z.enum(["gold", "adversarial"]),
    description: z.string().min(1).max(500),
    expectedValid: z.boolean(),
    expectedProductionEligible: z.boolean(),
    expectedIssueCodes: z.array(z.string().regex(/^[A-Z][A-Z0-9_]{2,99}$/)).max(100),
    observedEvaluation: MedicationSafetyEvaluationSchema,
})
    .strict();
export const MedicationEvaluationSuiteResultSchema = z
    .object({
    suiteId: IdSchema,
    suiteVersion: z.string().min(1).max(80),
    evaluatedAt: IsoDateTimeSchema,
    evaluatorVersion: z.string().min(1).max(80),
    cases: z.array(MedicationEvaluationCaseSchema).min(1).max(10_000),
    passed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    blockerFailures: z.number().int().nonnegative(),
    contentHash: Sha256Schema,
})
    .strict()
    .superRefine((suite, context) => {
    if (suite.passed + suite.failed !== suite.cases.length) {
        context.addIssue({
            code: "custom",
            path: ["failed"],
            message: "Evaluation pass and failure totals must equal the case count",
        });
    }
});
export const MedicationEvaluationSuiteReceiptSchema = z
    .object({
    suiteId: IdSchema,
    contentHash: Sha256Schema,
    passed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
})
    .strict();
export const MedicationPromotionReviewSchema = z
    .object({
    reviewId: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    manifestId: IdSchema,
    catalogueId: IdSchema,
    catalogueVersion: z.number().int().positive(),
    safetyRuleSetId: IdSchema,
    safetyRuleSetVersion: z.number().int().positive(),
    reviewerId: IdSchema,
    reviewerRole: z.literal("physician"),
    reviewerAal: z.literal("aal2"),
    decision: z.enum(["approved", "rejected"]),
    reviewedAt: IsoDateTimeSchema,
    evaluationSuiteId: IdSchema,
    evaluationSuiteHash: Sha256Schema,
    clinicalAttestation: z.string().min(20).max(2_000),
})
    .strict();
export const MedicationPromotionReceiptSchema = z
    .object({
    manifestId: IdSchema,
    status: z.literal("promoted"),
    reviewId: IdSchema,
})
    .strict();
export const PatientSafetyFactProvenanceSchema = z
    .object({
    sourceKind: z.enum([
        "patient_reported",
        "clinician_asserted",
        "laboratory_result",
        "medication_reconciliation",
        "external_document",
    ]),
    sourceId: IdSchema,
    sourceVersion: z.string().min(1).max(160),
    observedAt: IsoDateTimeSchema,
    recordedAt: IsoDateTimeSchema,
    reviewedAt: IsoDateTimeSchema.optional(),
    reviewedBy: IdSchema.optional(),
})
    .strict()
    .superRefine((provenance, context) => {
    if ((provenance.reviewedAt === undefined) !== (provenance.reviewedBy === undefined)) {
        context.addIssue({
            code: "custom",
            path: ["reviewedAt"],
            message: "Safety-fact review time and reviewer must be recorded together",
        });
    }
    if (Date.parse(provenance.recordedAt) < Date.parse(provenance.observedAt)) {
        context.addIssue({
            code: "custom",
            path: ["recordedAt"],
            message: "Safety-fact recording cannot precede observation",
        });
    }
    if (provenance.reviewedAt !== undefined &&
        Date.parse(provenance.reviewedAt) < Date.parse(provenance.recordedAt)) {
        context.addIssue({
            code: "custom",
            path: ["reviewedAt"],
            message: "Safety-fact review cannot precede recording",
        });
    }
});
export const PatientSafetyFactSchema = z
    .object({
    factId: IdSchema,
    tenantId: IdSchema,
    patientId: IdSchema,
    encounterId: IdSchema.optional(),
    kind: z.enum([
        "pregnancy_status",
        "hepatic_status",
        "renal_function",
        "allergy",
        "current_medication",
        "clinical_observation",
    ]),
    code: z.string().min(1).max(80),
    value: z.unknown(),
    status: z.enum(["active", "entered_in_error", "superseded"]),
    provenance: PatientSafetyFactProvenanceSchema,
    contentHash: Sha256Schema,
})
    .strict();
export const PatientSafetyFactReceiptSchema = z
    .object({
    factId: IdSchema,
    version: z.number().int().positive(),
    contentHash: Sha256Schema,
})
    .strict();
export const PrescriptionOperationSchema = z.enum(["new", "renew", "change", "cancel"]);
export const NcpdpScriptVersionSchema = z.enum(["2017071", "2023011"]);
export const PharmacyDestinationBindingSchema = z
    .object({
    destinationId: IdSchema,
    destinationVersion: z.number().int().positive(),
    ncpdpId: z.string().regex(/^[A-Za-z0-9-]{5,35}$/),
    display: z.string().min(1).max(240),
    endpointId: IdSchema,
    provider: z.enum(["fake_erx", "certified_erx_partner"]),
    mode: z.enum(["fake", "certified"]),
    scriptVersion: NcpdpScriptVersionSchema,
    supportsControlledSubstances: z.boolean(),
    verificationStatus: z.literal("verified"),
    certificationReference: z.string().min(1).max(500),
    verifiedAt: IsoDateTimeSchema,
    validUntil: IsoDateTimeSchema,
})
    .strict();
export const EpcsAuthorizationBindingSchema = z
    .object({
    authorizationId: IdSchema,
    practitionerId: IdSchema,
    identityProofingReference: z.string().min(1).max(500),
    twoFactorMethod: z.string().min(1).max(160),
    applicationCertificationReference: z.string().min(1).max(500),
    authorizedAt: IsoDateTimeSchema,
    expiresAt: IsoDateTimeSchema,
})
    .strict();
export const PrescriptionExecutionPackageSchema = z
    .object({
    packageId: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    patientId: IdSchema,
    encounterId: IdSchema,
    prescriptionId: IdSchema,
    prescriptionVersion: z.number().int().positive(),
    operation: PrescriptionOperationSchema,
    transactionId: IdSchema,
    approvalReceiptId: IdSchema,
    bundleId: IdSchema,
    bundleVersion: z.number().int().positive(),
    actionId: IdSchema,
    actionVersion: z.number().int().positive(),
    exactActionHash: Sha256Schema,
    fulfillmentId: IdSchema,
    expectedEventId: IdSchema,
    prescription: StructuredPrescriptionPayloadSchema,
    prescriptionHash: Sha256Schema,
    safetyEvaluation: MedicationSafetyEvaluationSchema,
    safetyContextHash: Sha256Schema,
    destination: PharmacyDestinationBindingSchema,
    epcsAuthorization: EpcsAuthorizationBindingSchema.optional(),
    dataClassification: z.enum(["synthetic", "phi"]),
    createdAt: IsoDateTimeSchema,
    packageContentHash: Sha256Schema,
})
    .strict()
    .superRefine((pkg, context) => {
    if (pkg.operation !== pkg.prescription.operation) {
        context.addIssue({
            code: "custom",
            path: ["operation"],
            message: "Prescription package operation must match the exact approved payload",
        });
    }
    if (pkg.prescription.pharmacyDestinationId !== pkg.destination.destinationId) {
        context.addIssue({
            code: "custom",
            path: ["destination"],
            message: "Prescription destination must match the exact package destination",
        });
    }
    if (pkg.safetyEvaluation.prescriptionHash !== pkg.prescriptionHash) {
        context.addIssue({
            code: "custom",
            path: ["safetyEvaluation", "prescriptionHash"],
            message: "Safety evaluation must bind the exact prescription payload",
        });
    }
    if (pkg.safetyEvaluation.contextHash !== pkg.safetyContextHash) {
        context.addIssue({
            code: "custom",
            path: ["safetyEvaluation", "contextHash"],
            message: "Safety evaluation must bind the exact patient safety context",
        });
    }
    const controlled = pkg.prescription.controlledSchedule !== undefined;
    if (controlled && pkg.epcsAuthorization === undefined) {
        context.addIssue({
            code: "custom",
            path: ["epcsAuthorization"],
            message: "Controlled prescriptions require an exact EPCS authorization binding",
        });
    }
});
export const PrescriptionDispatchReceiptSchema = z
    .object({
    tenantId: IdSchema,
    prescriptionId: IdSchema,
    prescriptionVersion: z.number().int().positive(),
    operation: PrescriptionOperationSchema,
    packageId: IdSchema,
    packageContentHash: Sha256Schema,
    transactionId: IdSchema,
    actionId: IdSchema,
    fulfillmentId: IdSchema,
    expectedEventId: IdSchema,
    destinationId: IdSchema,
    endpointId: IdSchema,
    partnerReferenceId: z.string().regex(/^[A-Za-z0-9._:-]{1,160}$/),
    idempotencyKey: z.string().min(8).max(200),
    outcome: z.enum(["sent", "duplicate"]),
    dispatchedAt: IsoDateTimeSchema,
})
    .strict();
const PrescriptionCallbackBaseSchema = z
    .object({
    tenantId: IdSchema,
    callbackId: IdSchema,
    provider: z.enum(["fake_erx", "certified_erx_partner"]),
    prescriptionId: IdSchema,
    prescriptionVersion: z.number().int().positive(),
    packageId: IdSchema,
    packageContentHash: Sha256Schema,
    transactionId: IdSchema,
    actionId: IdSchema,
    fulfillmentId: IdSchema,
    expectedEventId: IdSchema,
    partnerReferenceId: z.string().regex(/^[A-Za-z0-9._:-]{1,160}$/),
    occurredAt: IsoDateTimeSchema,
    receivedAt: IsoDateTimeSchema,
})
    .strict();
export const PrescriptionPartnerEventKindSchema = z.enum([
    "delivery_acknowledged",
    "pharmacy_accepted",
    "pharmacy_rejected",
    "dispense_status",
    "renewal_requested",
    "change_requested",
    "cancellation_acknowledged",
]);
export const PrescriptionPartnerCallbackSchema = z.discriminatedUnion("eventKind", [
    PrescriptionCallbackBaseSchema.extend({
        eventKind: z.literal("delivery_acknowledged"),
    }).strict(),
    PrescriptionCallbackBaseSchema.extend({
        eventKind: z.literal("pharmacy_accepted"),
        responseCode: z.string().min(1).max(80),
    }).strict(),
    PrescriptionCallbackBaseSchema.extend({
        eventKind: z.literal("pharmacy_rejected"),
        rejectionCode: z.string().regex(/^[A-Z][A-Z0-9_]{0,63}$/),
        retryable: z.boolean(),
    }).strict(),
    PrescriptionCallbackBaseSchema.extend({
        eventKind: z.literal("dispense_status"),
        dispenseStatus: z.enum(["filled", "partially_filled", "not_filled", "transferred"]),
    }).strict(),
    PrescriptionCallbackBaseSchema.extend({
        eventKind: z.literal("renewal_requested"),
        requestReferenceId: z.string().regex(/^[A-Za-z0-9._:-]{1,160}$/),
    }).strict(),
    PrescriptionCallbackBaseSchema.extend({
        eventKind: z.literal("change_requested"),
        requestReferenceId: z.string().regex(/^[A-Za-z0-9._:-]{1,160}$/),
        reasonCode: z.string().regex(/^[A-Z][A-Z0-9_]{0,63}$/),
    }).strict(),
    PrescriptionCallbackBaseSchema.extend({
        eventKind: z.literal("cancellation_acknowledged"),
        responseCode: z.string().min(1).max(80),
    }).strict(),
]);
export const PrescriptionWorkflowStatusSchema = z.enum([
    "locally_accepted",
    "queued",
    "sent",
    "delivery_acknowledged",
    "pharmacy_accepted",
    "pharmacy_rejected",
    "partially_filled",
    "filled",
    "not_filled",
    "renewal_requested",
    "change_requested",
    "cancellation_pending",
    "cancelled",
    "patient_contact_pending",
    "closed",
]);
export const PrescriptionFollowUpSchema = z
    .object({
    followUpId: IdSchema,
    prescriptionId: IdSchema,
    ownerId: IdSchema,
    ownerRole: z.enum(["physician", "nurse_ma"]),
    dueAt: IsoDateTimeSchema,
    escalationAt: IsoDateTimeSchema,
    status: z.enum(["open", "completed", "escalated", "cancelled"]),
    reasonCode: z.string().regex(/^[A-Z][A-Z0-9_]{2,99}$/),
    completedAt: IsoDateTimeSchema.optional(),
})
    .strict()
    .superRefine((followUp, context) => {
    if (Date.parse(followUp.escalationAt) < Date.parse(followUp.dueAt)) {
        context.addIssue({
            code: "custom",
            path: ["escalationAt"],
            message: "Prescription escalation cannot precede its due time",
        });
    }
});
export const PrescriptionWorkflowSchema = z
    .object({
    tenantId: IdSchema,
    prescriptionId: IdSchema,
    version: z.number().int().positive(),
    actionId: IdSchema,
    operation: PrescriptionOperationSchema,
    status: PrescriptionWorkflowStatusSchema,
    partnerReferenceId: z
        .string()
        .regex(/^[A-Za-z0-9._:-]{1,160}$/)
        .optional(),
    followUps: z.array(PrescriptionFollowUpSchema).max(100),
    updatedAt: IsoDateTimeSchema,
    closedAt: IsoDateTimeSchema.optional(),
})
    .strict();
export const PharmacyDestinationSummarySchema = PharmacyDestinationBindingSchema.omit({
    endpointId: true,
}).extend({
    dataClassification: z.enum(["synthetic", "phi"]),
});
export const PharmacyDestinationListSchema = z.array(PharmacyDestinationSummarySchema).max(1_000);
export const PrescriptionCaseFollowUpSchema = z
    .object({
    followUpId: IdSchema,
    ownerId: IdSchema,
    ownerRole: z.enum(["physician", "nurse_ma"]),
    reasonCode: z.string().regex(/^[A-Z][A-Z0-9_]{2,99}$/),
    status: z.enum(["open", "completed", "escalated", "cancelled"]),
    dueAt: IsoDateTimeSchema,
    escalationAt: IsoDateTimeSchema,
    completedAt: IsoDateTimeSchema.nullable().optional(),
})
    .strict();
export const PrescriptionCaseCallbackSchema = z
    .object({
    callbackId: IdSchema,
    eventKind: PrescriptionPartnerEventKindSchema,
    state: z.enum(["received", "applied", "duplicate", "conflict"]),
    occurredAt: IsoDateTimeSchema,
    receivedAt: IsoDateTimeSchema,
})
    .strict();
export const PrescriptionCommunicationSchema = z
    .object({
    communicationId: IdSchema,
    channel: z.enum(["phone", "portal", "in_person", "other"]),
    outcome: z.enum(["reached", "message_left", "not_reached", "declined"]),
    reasonCode: z.string().regex(/^[A-Z][A-Z0-9_]{2,99}$/),
    recordedBy: IdSchema,
    recordedAt: IsoDateTimeSchema,
})
    .strict();
export const PrescriptionTransitionSchema = z
    .object({
    transitionId: z.coerce.number().int().positive(),
    prescriptionVersion: z.coerce.number().int().positive(),
    fromState: z.string().min(1).max(80),
    toState: PrescriptionWorkflowStatusSchema,
    eventKind: z.string().min(1).max(80),
    reasonCode: z.string().regex(/^[A-Z][A-Z0-9_]{2,99}$/),
    occurredAt: IsoDateTimeSchema,
})
    .strict();
export const PrescriptionExpectedEventSummarySchema = z
    .object({
    expectedEventId: IdSchema,
    status: z.string().min(1).max(80),
    version: z.coerce.number().int().positive(),
    expectedBy: IsoDateTimeSchema,
    overdueAt: IsoDateTimeSchema,
    ownerId: IdSchema,
    ownerRole: z.string().min(1).max(80),
})
    .strict();
export const PrescriptionCaseSchema = z
    .object({
    tenantId: IdSchema,
    prescriptionId: IdSchema,
    version: z.coerce.number().int().positive(),
    siteId: IdSchema,
    patientId: IdSchema,
    encounterId: IdSchema,
    bundleId: IdSchema,
    actionId: IdSchema,
    actionVersion: z.coerce.number().int().positive(),
    operation: PrescriptionOperationSchema,
    sourcePrescriptionId: IdSchema.nullable().optional(),
    status: PrescriptionWorkflowStatusSchema,
    partnerReferenceId: z
        .string()
        .regex(/^[A-Za-z0-9._:-]{1,160}$/)
        .nullable()
        .optional(),
    executionPackage: PrescriptionExecutionPackageSchema,
    expectedEvent: PrescriptionExpectedEventSummarySchema,
    followUps: z.array(PrescriptionCaseFollowUpSchema).max(1_000),
    callbacks: z.array(PrescriptionCaseCallbackSchema).max(10_000),
    communications: z.array(PrescriptionCommunicationSchema).max(1_000),
    transitions: z.array(PrescriptionTransitionSchema).max(10_000),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    closedAt: IsoDateTimeSchema.nullable().optional(),
})
    .strict();
export const PrescriptionWorkQueueItemSchema = z
    .object({
    prescriptionId: IdSchema,
    version: z.coerce.number().int().positive(),
    patientId: IdSchema,
    operation: PrescriptionOperationSchema,
    status: PrescriptionWorkflowStatusSchema,
    dueAt: IsoDateTimeSchema,
    escalationAt: IsoDateTimeSchema,
    reasonCode: z.string().regex(/^[A-Z][A-Z0-9_]{2,99}$/),
})
    .strict();
export const PrescriptionWorkQueueSchema = z.array(PrescriptionWorkQueueItemSchema).max(1_000);
export const RecordPrescriptionSafetyEvaluationRequestSchema = z
    .object({
    actionId: IdSchema,
    actionVersion: z.number().int().positive(),
    context: MedicationSafetyContextSchema,
    evaluation: MedicationSafetyEvaluationSchema,
    evaluatorVersion: z.string().min(1).max(80),
    idempotencyKey: IdSchema,
})
    .strict();
export const PrescriptionSafetyEvaluationReceiptSchema = z
    .object({
    evaluationId: IdSchema,
    prescriptionHash: Sha256Schema,
    contextHash: Sha256Schema,
    productionEligible: z.boolean(),
})
    .strict();
export const RecordPrescriptionCommunicationRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    channel: z.enum(["phone", "portal", "in_person", "other"]),
    outcome: z.enum(["reached", "message_left", "not_reached", "declined"]),
    reasonCode: z.string().regex(/^[A-Z][A-Z0-9_]{2,99}$/),
    idempotencyKey: IdSchema,
})
    .strict();
export const CompletePrescriptionFollowUpRequestSchema = z
    .object({ expectedVersion: z.number().int().positive() })
    .strict();
export const ClosePrescriptionRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    reasonCode: z.string().regex(/^[A-Z][A-Z0-9_]{2,99}$/),
    idempotencyKey: IdSchema,
})
    .strict();
//# sourceMappingURL=prescriptions.js.map